import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { orderAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Separator } from '../../components/ui/separator';
import { toast } from 'sonner';
import {
  ShoppingCart,
  Trash2,
  Plus,
  Minus,
  MapPin,
  CreditCard,
  Loader2,
  ArrowLeft,
  ChefHat
} from 'lucide-react';
import BuyerLayout from '../../components/layouts/BuyerLayout';

const FOOD_PLACEHOLDER = "https://images.unsplash.com/photo-1672477179695-7276b0602fa9?w=100";

export default function CartPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { items, updateQuantity, removeItem, clearCart, getTotal } = useCart();
  
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  const handlePlaceOrder = async () => {
    if (!deliveryAddress.trim()) {
      toast.error('Please enter delivery address');
      return;
    }
    
    if (items.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    setLoading(true);
    try {
      const orderItems = items.map(item => ({
        item_id: item.id,
        item_name: item.name,
        quantity: item.quantity,
        price: item.is_free ? 0 : item.price,
        seller_id: item.seller_id
      }));

      const orderData = {
        items: orderItems,
        delivery_address: deliveryAddress.trim(),
        notes: notes.trim() || null
      };

      await orderAPI.create(orderData, user.id);
      clearCart();
      toast.success('Order placed successfully!');
      navigate('/buyer/orders');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to place order');
    } finally {
      setLoading(false);
    }
  };

  const subtotal = getTotal();
  const deliveryFee = 0; // Free delivery within complex
  const total = subtotal + deliveryFee;

  if (items.length === 0) {
    return (
      <BuyerLayout>
        <div className="empty-state py-20">
          <ShoppingCart className="empty-state-icon" />
          <h3 className="font-heading text-xl font-semibold mb-2">Your cart is empty</h3>
          <p className="text-muted-foreground mb-6">Add some delicious items to get started</p>
          <Button onClick={() => navigate('/buyer')} className="rounded-full">
            Browse Menu
          </Button>
        </div>
      </BuyerLayout>
    );
  }

  return (
    <BuyerLayout>
      <div className="space-y-6 pb-mobile-nav">
        {/* Header */}
        <div className="flex items-center gap-3">
          <button 
            onClick={() => navigate('/buyer')}
            className="p-2 hover:bg-muted rounded-full transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="font-heading text-2xl font-bold">Your Cart</h1>
            <p className="text-sm text-muted-foreground">{items.length} items</p>
          </div>
        </div>

        {/* Cart Items */}
        <Card>
          <CardContent className="p-4 space-y-4">
            {items.map((item, idx) => (
              <React.Fragment key={item.id}>
                <div className="flex gap-4" data-testid={`cart-item-${item.id}`}>
                  <div className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0">
                    <img
                      src={item.image_url || FOOD_PLACEHOLDER}
                      alt={item.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-foreground">{item.name}</h3>
                        <p className="text-sm text-muted-foreground">
                          {item.is_free ? 'Free' : `₹${item.price}`} each
                        </p>
                      </div>
                      <button
                        onClick={() => removeItem(item.id)}
                        className="p-1 text-destructive hover:bg-destructive/10 rounded"
                        data-testid={`remove-${item.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <div className="quantity-selector">
                        <button 
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          data-testid={`cart-decrease-${item.id}`}
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span>{item.quantity}</span>
                        <button 
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          data-testid={`cart-increase-${item.id}`}
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                      <span className="font-semibold">
                        {item.is_free ? 'Free' : `₹${item.price * item.quantity}`}
                      </span>
                    </div>
                  </div>
                </div>
                {idx < items.length - 1 && <Separator />}
              </React.Fragment>
            ))}
          </CardContent>
        </Card>

        {/* Delivery Details */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <MapPin className="w-5 h-5 text-primary" />
              Delivery Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="address">Delivery Address *</Label>
              <Textarea
                id="address"
                data-testid="delivery-address"
                placeholder="Enter your flat/tower number and any landmarks..."
                value={deliveryAddress}
                onChange={(e) => setDeliveryAddress(e.target.value)}
                className="resize-none"
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes">Special Instructions (Optional)</Label>
              <Input
                id="notes"
                data-testid="order-notes"
                placeholder="Any special requests..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Bill Summary */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Bill Summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Item Total</span>
              <span>₹{subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Delivery Fee</span>
              <span className="text-secondary font-medium">FREE</span>
            </div>
            <Separator />
            <div className="flex justify-between font-semibold text-lg">
              <span>Grand Total</span>
              <span className="text-primary">₹{total.toFixed(2)}</span>
            </div>
            <p className="text-xs text-muted-foreground text-center">
              * Cash on Delivery only. Pay when your food arrives.
            </p>
          </CardContent>
        </Card>

        {/* Place Order Button */}
        <Button
          size="lg"
          onClick={handlePlaceOrder}
          disabled={loading || !deliveryAddress.trim()}
          className="w-full h-14 rounded-2xl text-lg font-bold"
          data-testid="place-order-btn"
        >
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <>
              <CreditCard className="w-5 h-5 mr-2" />
              Place Order • ₹{total.toFixed(2)}
            </>
          )}
        </Button>
      </div>
    </BuyerLayout>
  );
}
